import { observer } from 'mobx-react';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { MultiSelect } from 'primereact/multiselect';
import { Paginator } from 'primereact/paginator';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authServices, User } from 'services/authServices';
import {
  AuditTrailEntry,
  BidAnalysisProject,
  bidAnalysisServices,
  DashboardAnalytics,
} from 'services/bidAnalysis/bidAnalysisServices';
import classes from './bidAnalysisDashboard.module.scss';

const BidAnalysisDashboard = () => {
  const navigate = useNavigate();

  const [_analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [activeProjects, setActiveProjects] = useState<BidAnalysisProject[]>([]);
  const [recentActivity, setRecentActivity] = useState<AuditTrailEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [bidsLoading, setBidsLoading] = useState(false); // Loading state for bids search/filter
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Track initial load to prevent double call

  // Pagination and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string[]>([
    'Draft',
    'In Review',
    'Needs modification',
    'Approved',
  ]); // Selected statuses in dropdown
  const [appliedStatusFilter, setAppliedStatusFilter] = useState<string[]>([
    'Draft',
    'In Review',
    'Needs modification',
    'Approved',
  ]); // Applied statuses for API call
  const [appliedSearchTerm, setAppliedSearchTerm] = useState(''); // Applied search term for API call
  const [userFilter, setUserFilter] = useState<string[]>([]); // Selected users in dropdown
  const [appliedUserFilter, setAppliedUserFilter] = useState<string[]>([]); // Applied users for API call
  const [usersList, setUsersList] = useState<User[]>([]); // List of all users from API
  const [usersLoading, setUsersLoading] = useState(false); // Loading state for users list
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [pageSize] = useState(6); // Always use 6 items per page

  // Recent Activity pagination states
  const [activityCurrentPage, setActivityCurrentPage] = useState(0);
  const [activityTotalRecords, setActivityTotalRecords] = useState(0);
  const [activityPageSize] = useState(5); // 5 items per page for recent activity
  const [activityLoading, setActivityLoading] = useState(false);
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards'); // Toggle between cards and list view

  // Status options for multi-select filter dropdown
  const statusOptions = [
    { label: 'Draft', value: 'Draft' },
    { label: 'In Review', value: 'In Review' },
    { label: 'Needs modification', value: 'Needs modification' },
    { label: 'Approved', value: 'Approved' },
    { label: 'Closed', value: 'Closed' },
  ];

  useEffect(() => {
    loadDashboardData();
    loadRecentActivity(0); // Load first page on initial mount
    loadUsersList(); // Load users list for filter
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Only reload when applied filters or page changes (not when filter selection changes)
    // Skip on initial load to avoid double call
    if (!isInitialLoad) {
      loadActiveBids();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedSearchTerm, appliedStatusFilter, appliedUserFilter, currentPage]);

  // Don't include loadActiveBids and loading in dependencies to avoid infinite loop

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const analyticsData = await bidAnalysisServices.getDashboardAnalytics();
      setAnalytics(analyticsData);

      // Load active bids separately with pagination
      await loadActiveBids();
      setIsInitialLoad(false); // Mark initial load as complete
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      // Set empty arrays on error to prevent map errors
      setActiveProjects([]);
      setIsInitialLoad(false);
    } finally {
      setLoading(false);
    }
  };

  const loadRecentActivity = async (page: number = 0) => {
    setActivityLoading(true);
    try {
      // Frontend page 0 = Backend page 1
      const backendPage = page + 1;
      const response = await bidAnalysisServices.getLatestAuditTrail({
        page: backendPage,
        page_size: activityPageSize, // 5 items per page
      });

      // Ensure we only show 5 items maximum (slice in case backend returns more)
      const activityData = response.data || [];
      const limitedData = activityData.slice(0, activityPageSize);

      setRecentActivity(limitedData);
      setActivityTotalRecords(response.pagination?.total_count || activityData.length || 0);
    } catch (error) {
      console.error('Error loading recent activity:', error);
      setRecentActivity([]);
      setActivityTotalRecords(0);
    } finally {
      setActivityLoading(false);
    }
  };

  const onActivityPageChange = (event: any) => {
    // PrimeReact Paginator provides first and rows
    const page = Math.floor(event.first / event.rows);
    setActivityCurrentPage(page);
    loadRecentActivity(page);
  };

  const getActivityRowsPerPage = () => {
    return activityPageSize;
  };

  const getActivityFirstRecord = () => {
    return activityCurrentPage * activityPageSize;
  };

  const loadUsersList = async () => {
    setUsersLoading(true);
    try {
      const response = await authServices.getUsersList();
      setUsersList(response.users || []);
    } catch (error) {
      console.error('Error loading users list:', error);
      setUsersList([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const loadActiveBids = async () => {
    setBidsLoading(true);
    try {
      // Simplified pagination logic: Always use page_size=6
      // Frontend page 0 = Backend page 1 (items 1-6)
      // Frontend page 1 = Backend page 2 (items 7-12)
      // Frontend page 2 = Backend page 3 (items 13-18)
      // etc.
      const backendPage = currentPage + 1;
      const backendPageSize = pageSize; // Always 6

      // Convert appliedStatusFilter array to comma-separated string for API
      // Default: all statuses except Closed
      let statusFilterValue: string | undefined = undefined;
      if (appliedStatusFilter && appliedStatusFilter.length > 0) {
        statusFilterValue = appliedStatusFilter.join(',');
      } else {
        // If no statuses selected, query all except Closed (default behavior)
        statusFilterValue = 'Draft,In Review,Needs modification,Approved';
      }

      // Convert appliedUserFilter array to comma-separated string for API
      let userFilterValue: string | undefined = undefined;
      if (appliedUserFilter && appliedUserFilter.length > 0) {
        userFilterValue = appliedUserFilter.join(',');
      }

      const result = await bidAnalysisServices.getActiveBids({
        page: backendPage,
        page_size: backendPageSize,
        search: appliedSearchTerm.trim() || undefined,
        status: statusFilterValue,
        user: userFilterValue,
      });

      setActiveProjects(result.data);
      // Use count from result (which comes from pagination.total_count)
      // Ensure we're using the total count, not the current page data length
      const totalCount = result.count || 0;
      setTotalRecords(totalCount);
    } catch (error) {
      console.error('Error loading active bids:', error);
      setActiveProjects([]);
      setTotalRecords(0);
    } finally {
      setBidsLoading(false);
    }
  };

  const onPageChange = (event: any) => {
    // PrimeReact Paginator provides first and rows
    // Since we always use pageSize=6, the calculation is simple
    // Page = first / rows
    const page = Math.floor(event.first / event.rows);
    setCurrentPage(page);
  };

  // Calculate the rows per page for paginator display (always 6)
  const getRowsPerPage = () => {
    return pageSize; // Always 6
  };

  // Calculate the first record index for paginator
  const getFirstRecord = () => {
    return currentPage * pageSize;
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    // Don't reset page or apply filter automatically
  };

  const handleStatusFilterChange = (e: { value: string[] }) => {
    // MultiSelect passes array of selected values
    const selectedStatuses = e.value || [];
    setStatusFilter(selectedStatuses);
    // Don't reset page or apply filter automatically
  };

  const handleUserFilterChange = (e: { value: string[] }) => {
    // MultiSelect passes array of selected user names
    const selectedUsers = e.value || [];
    setUserFilter(selectedUsers);
    // Don't reset page or apply filter automatically
  };

  const handleApplyFilter = () => {
    // Apply the selected filters and reset to first page
    setAppliedSearchTerm(searchTerm);
    setAppliedStatusFilter(statusFilter);
    setAppliedUserFilter(userFilter);
    setCurrentPage(0);
  };

  const getStatusClass = (status: string, originalStatus?: string) => {
    // Use originalStatus if available, otherwise use status
    const statusToCheck = originalStatus?.toLowerCase() || status.toLowerCase();

    switch (statusToCheck) {
      case 'draft':
        return classes.statusDraft;
      case 'review':
      case 'in review':
      case 'in-review':
        return classes.statusReview;
      case 'needs modification':
      case 'needs-modification':
        return classes.statusNeedsModification;
      case 'approved':
        return classes.statusApproved;
      case 'closed':
        return classes.statusClosed;
      default:
        return '';
    }
  };

  const getActivityIconClass = (eventType: string) => {
    // Match backend event types from constants.py:
    // - "New Bid Creation"
    // - "Submit for Review"
    // - "Analysis Updated"
    // - "Analysis Approved" (TODO: not yet implemented)
    const type = eventType.toLowerCase();
    if (type === 'submit for review') {
      return classes.activityIconSubmitted;
    } else if (type === 'analysis updated') {
      return classes.activityIconUpdated;
    } else if (type === 'analysis approved') {
      return classes.activityIconApproved;
    } else if (type === 'new bid creation') {
      return classes.activityIconCreated;
    }
    // Fallback for any other event types
    return classes.activityIconUpdated;
  };

  const getActivityIcon = (eventType: string) => {
    // Match backend event types from constants.py:
    // - "New Bid Creation"
    // - "Submit for Review"
    // - "Analysis Updated"
    // - "Analysis Approved" (TODO: not yet implemented)
    const type = eventType.toLowerCase();
    if (type === 'submit for review') {
      return 'pi pi-send';
    } else if (type === 'analysis updated') {
      return 'pi pi-pencil';
    } else if (type === 'analysis approved') {
      return 'pi pi-check-circle';
    } else if (type === 'new bid creation') {
      return 'pi pi-plus';
    }
    // Fallback for any other event types
    return 'pi pi-info-circle';
  };

  const formatActivityDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;

      // Format as: YYYY-MM-DD HH:MM
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <i className="pi pi-spinner pi-spin" style={{ fontSize: '3rem', color: '#522d6e' }}></i>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className={classes.dashboardContainer}>
      <div className={classes.contentContainer}>
        {/* Hero Section */}
        <section className={classes.dashboardHero}>
          <h1>Government Bid Analysis Dashboard</h1>
          <p>
            Streamline your pharmaceutical product bid analysis with automated calculations, market
            intelligence, and collaborative workflow management.
          </p>

          {/* Role-based Quick Access */}
          <div className={classes.roleAccessContainer}>
            <h3>Quick Access by Role</h3>
            <div className={classes.roleAccessGrid}>
              <div className={classes.roleCard}>
                <i className="pi pi-user-edit" style={{ fontSize: '2.5rem', color: '#522d6e' }}></i>
                <h4>Business Analysts</h4>
                <p>Create and manage bid analyses</p>
                <Button
                  label="Start New Analysis"
                  onClick={() => navigate('/bdportal/bid-analysis/wizard/contract-type')}
                  className={classes.roleButton}
                />
              </div>
              <div className={classes.roleCard}>
                <i className="pi pi-verified" style={{ fontSize: '2.5rem', color: '#5b8def' }}></i>
                <h4>Account Managers</h4>
                <p>Review and approve submissions</p>
                <Button
                  label="Review Dashboard"
                  onClick={() => navigate('/bdportal/bid-analysis/manager-review')}
                  className={classes.roleButton}
                  style={{ background: '#5b8def', border: 'none' }}
                />
              </div>
              <div className={classes.roleCard}>
                <i className="pi pi-shield" style={{ fontSize: '2.5rem', color: '#10b981' }}></i>
                <h4>P&C Team</h4>
                <p>Validation and oversight</p>
                <Button label="P&C Dashboard" className={classes.roleButton} severity="success" />
              </div>
            </div>
          </div>
        </section>

        {/* Quick Actions */}
        <section className={classes.quickActions}>
          <div
            className={`${classes.actionCard} ${classes.actionCardPrimary}`}
            onClick={() => navigate('/bdportal/bid-analysis/wizard/contract-type')}
          >
            <i className="pi pi-plus-circle"></i>
            <h3>New Bid Analysis</h3>
            <p>Create a new government bid analysis using our guided template selection wizard</p>
          </div>

          <div
            className={classes.actionCard}
            onClick={() => navigate('/bdportal/bid-analysis/reference-tables')}
          >
            <i className="pi pi-table"></i>
            <h3>Reference Tables</h3>
            <p>Manage attributes and user-attribute mappings</p>
          </div>

          {/* Template Library - Commented out per user request */}
          {/* <div className={classes.actionCard}>
            <i className="pi pi-folder-open"></i>
            <h3>Template Library</h3>
            <p>Browse and manage analysis templates</p>
          </div> */}

          {/* Reports & Analytics - Commented out per user request */}
          {/* <div className={classes.actionCard}>
            <i className="pi pi-chart-bar"></i>
            <h3>Reports & Analytics</h3>
            <p>View comprehensive reports and performance metrics</p>
          </div> */}
        </section>

        {/* Active Projects */}
        <section className={classes.dashboardSection}>
          <div className={classes.sectionHeader}>
            <h2>Active Projects</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Button
                icon={viewMode === 'cards' ? 'pi pi-th-large' : 'pi pi-list'}
                onClick={() => setViewMode(viewMode === 'cards' ? 'list' : 'cards')}
                tooltip={viewMode === 'cards' ? 'Switch to List View' : 'Switch to Card View'}
                tooltipOptions={{ position: 'bottom' }}
                className="p-button-text p-button-plain"
                style={{
                  color: '#522d6e',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  padding: '0.5rem',
                  minWidth: 'auto',
                  height: '2.5rem',
                }}
              />
            </div>
          </div>
          <div className={classes.sectionContent}>
            {/* Search and Filter Controls */}
            <div className={classes.filterControls}>
              {/* Search bar and Apply Filter button on same row */}
              <div
                style={{
                  display: 'flex',
                  gap: '0.5rem',
                  alignItems: 'center',
                  width: '100%',
                  marginBottom: '1rem',
                }}
              >
                <div className={classes.searchContainer} style={{ flex: 1 }}>
                  <i
                    className="pi pi-search"
                    style={{
                      position: 'absolute',
                      left: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      color: '#6b7280',
                    }}
                  ></i>
                  <InputText
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search by product name..."
                    className={classes.searchInput}
                    style={{ paddingLeft: '2.5rem' }}
                    disabled={bidsLoading}
                  />
                </div>
                <Button
                  label="Apply Filter"
                  icon="pi pi-filter"
                  onClick={handleApplyFilter}
                  disabled={bidsLoading}
                  style={{
                    padding: '0.625rem 0.75rem',
                    fontSize: '0.875rem',
                    height: '2.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: '#ffffff',
                    color: '#374151',
                    whiteSpace: 'nowrap',
                  }}
                />
              </div>
              {/* Status and Analyst filters on separate row */}
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <MultiSelect
                  value={statusFilter}
                  options={statusOptions}
                  onChange={handleStatusFilterChange}
                  placeholder="Filter by status"
                  className={classes.statusFilter}
                  style={{ minWidth: '200px' }}
                  disabled={bidsLoading}
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  selectedItemsLabel="{0} statuses selected"
                  showClear
                />
                <MultiSelect
                  value={userFilter}
                  options={usersList.map(user => ({ label: user.name, value: user.name }))}
                  onChange={handleUserFilterChange}
                  placeholder="Filter by analyst"
                  className={classes.statusFilter}
                  style={{ minWidth: '200px' }}
                  disabled={bidsLoading || usersLoading}
                  optionLabel="label"
                  optionValue="value"
                  display="chip"
                  selectedItemsLabel="{0} analysts selected"
                  showClear
                  filter
                />
              </div>
            </div>

            {/* Loading State */}
            {bidsLoading && (
              <div className={classes.loadingState}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '2rem', color: '#522d6e' }}
                ></i>
                <p>Loading bids...</p>
              </div>
            )}

            {/* No Records Found */}
            {!bidsLoading && activeProjects.length === 0 && (
              <div className={classes.noRecordsFound}>
                <i
                  className="pi pi-inbox"
                  style={{ fontSize: '3rem', color: '#9ca3af', marginBottom: '1rem' }}
                ></i>
                <h3>No Active Analyses Found</h3>
                <p>
                  {searchTerm || statusFilter
                    ? 'No bids match your search criteria. Try adjusting your filters.'
                    : 'There are no active bid analyses at the moment.'}
                </p>
              </div>
            )}

            {/* Projects Grid or List */}
            {!bidsLoading && activeProjects.length > 0 && (
              <div className={viewMode === 'cards' ? classes.projectsGrid : classes.projectsList}>
                {activeProjects.map(project => (
                  <div key={project.id} className={classes.projectCard}>
                    <div className={classes.projectHeader}>
                      <div>
                        <div className={classes.projectName}>{project.name}</div>
                        <div className={classes.projectType}>{project.contractType}</div>
                        <div className={classes.projectId} title={`Bid ID: ${project.id}`}>
                          Bid ID: {project.id}
                        </div>
                      </div>
                      <span
                        className={`${classes.projectStatus} ${getStatusClass(project.status, project.originalStatus)}`}
                      >
                        {project.originalStatus
                          ? project.originalStatus.toUpperCase()
                          : project.status.toUpperCase()}
                      </span>
                    </div>
                    <div className={classes.projectDetails}>
                      <div className={classes.projectDetail}>
                        <span className={classes.label}>Product:</span>
                        <span className={classes.value}>{project.productName}</span>
                      </div>
                      <div className={classes.projectDetail}>
                        <span className={classes.label}>Analyst:</span>
                        <span className={classes.value}>{project.analyst}</span>
                      </div>
                      {project.targetLaunch && (
                        <div className={classes.projectDetail}>
                          <span className={classes.label}>Target Launch:</span>
                          <span className={classes.value}>{project.targetLaunch}</span>
                        </div>
                      )}
                      {project.lastModified && (
                        <div className={classes.projectDetail}>
                          <span className={classes.label}>Last Modified:</span>
                          <span className={classes.value}>{project.lastModified}</span>
                        </div>
                      )}
                    </div>
                    <div className={classes.projectProgress}>
                      <div className={classes.progressLabel}>
                        <span>Progress</span>
                        <span>{project.progress}%</span>
                      </div>
                      <div className={classes.progressBar}>
                        <div
                          className={`${classes.progressFill} ${
                            project.status === 'draft'
                              ? classes.progressDraft
                              : project.status === 'review'
                                ? classes.progressReview
                                : classes.progressApproved
                          }`}
                          style={{ width: `${project.progress}%` }}
                        ></div>
                      </div>
                    </div>
                    <div className={classes.projectActions}>
                      {project.status === 'draft' && (
                        <>
                          <Button
                            label="Continue"
                            icon="pi pi-pencil"
                            size="small"
                            className={classes.continueButton}
                            onClick={() =>
                              navigate(`/bdportal/bid-analysis/template/${project.id}`)
                            }
                          />
                          {/* Share button commented out per user request */}
                          {/* <Button
                            label="Share"
                            icon="pi pi-share-alt"
                            size="small"
                            outlined
                            className={classes.shareButton}
                          /> */}
                        </>
                      )}
                      {project.status === 'review' && (
                        <>
                          <Button
                            label="View"
                            icon="pi pi-eye"
                            size="small"
                            className={classes.viewButton}
                            onClick={() =>
                              navigate(`/bdportal/bid-analysis/template/${project.id}`)
                            }
                          />
                          {/* Comment button commented out per user request */}
                          {/* <Button
                            label="Comment"
                            icon="pi pi-comment"
                            size="small"
                            outlined
                            className={classes.commentButton}
                          /> */}
                        </>
                      )}
                      {project.status === 'approved' && (
                        <>
                          <Button
                            label="View"
                            icon="pi pi-eye"
                            size="small"
                            className={classes.viewButton}
                            onClick={() =>
                              navigate(`/bdportal/bid-analysis/template/${project.id}`)
                            }
                          />
                          {/* Clone button commented out per user request */}
                          {/* <Button
                            label="Clone"
                            icon="pi pi-copy"
                            size="small"
                            outlined
                            className={classes.cloneButton}
                          /> */}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination - Show if there are more records than what can be displayed on one page */}
            {!bidsLoading && totalRecords > getRowsPerPage() && totalRecords > 0 && (
              <div className={classes.paginationContainer}>
                <Paginator
                  first={getFirstRecord()}
                  rows={getRowsPerPage()}
                  totalRecords={totalRecords}
                  onPageChange={onPageChange}
                  template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                  pageLinkSize={5}
                />
              </div>
            )}
          </div>
        </section>

        {/* Analytics Overview - Commented out per user request */}
        {/* {analytics && (
          <section className={classes.dashboardSection} style={{ position: 'relative' }}>
            <div className={classes.sectionHeader}>
              <h2>Analytics Overview</h2>
            </div>
            <div className={classes.sectionContent}>
              <div className={classes.analyticsGrid}>
                <div className={classes.metricCard}>
                  <div className={classes.metricValue}>{analytics.totalAnalyses}</div>
                  <div className={classes.metricLabel}>Analyses Completed</div>
                  <div className={`${classes.metricChange} ${classes.metricChangePositive}`}>
                    +3 vs last month
                  </div>
                </div>

                <div className={classes.metricCard}>
                  <div className={classes.metricValue}>{analytics.successRate}%</div>
                  <div className={classes.metricLabel}>Success Rate</div>
                  <div className={`${classes.metricChange} ${classes.metricChangePositive}`}>
                    +2% vs last month
                  </div>
                </div>

                <div className={classes.metricCard}>
                  <div className={classes.metricValue}>{analytics.avgDaysToComplete}</div>
                  <div className={classes.metricLabel}>Avg Days to Complete</div>
                  <div className={`${classes.metricChange} ${classes.metricChangeNeutral}`}>
                    -0.3 days
                  </div>
                </div>

                <div className={classes.metricCard}>
                  <div className={classes.metricValue}>{analytics.totalOpportunityValue}</div>
                  <div className={classes.metricLabel}>Total Opportunity Value</div>
                  <div className={`${classes.metricChange} ${classes.metricChangePositive}`}>
                    +15% vs last month
                  </div>
                </div>
              </div>
            </div>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '8px',
                zIndex: 10,
              }}
            >
              <div
                style={{
                  backgroundColor: '#5b5fd6',
                  color: 'white',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                }}
              >
                Coming Soon
              </div>
            </div>
          </section>
        )} */}

        {/* Recent Activity */}
        <section className={classes.dashboardSection}>
          <div className={classes.sectionHeader}>
            <h2>Recent Activity</h2>
          </div>
          <div className={classes.sectionContent}>
            {activityLoading ? (
              <div className={classes.loadingState}>
                <i
                  className="pi pi-spinner pi-spin"
                  style={{ fontSize: '2rem', color: '#522d6e' }}
                ></i>
                <p>Loading recent activity...</p>
              </div>
            ) : recentActivity.length === 0 ? (
              <div className={classes.noRecordsFound}>
                <i
                  className="pi pi-inbox"
                  style={{ fontSize: '3rem', color: '#9ca3af', marginBottom: '1rem' }}
                ></i>
                <p>No recent activity found</p>
              </div>
            ) : (
              <>
                <ul className={classes.activityList}>
                  {recentActivity.slice(0, activityPageSize).map(activity => (
                    <li key={activity.id} className={classes.activityItem}>
                      <div
                        className={`${classes.activityIcon} ${getActivityIconClass(activity.event_type)}`}
                      >
                        <i className={getActivityIcon(activity.event_type)}></i>
                      </div>
                      <div className={classes.activityContent}>
                        <div className={classes.activityTitle}>{activity.event_type}</div>
                        <div className={classes.activityDetails}>{activity.description}</div>
                        <div className={classes.activityUser}>by {activity.performed_by}</div>
                      </div>
                      <div className={classes.activityTime}>
                        {formatActivityDate(activity.performed_at)}
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Pagination - Show if there are multiple pages */}
                {!activityLoading &&
                  activityTotalRecords > getActivityRowsPerPage() &&
                  activityTotalRecords > 0 && (
                    <div className={classes.paginationContainer}>
                      <Paginator
                        first={getActivityFirstRecord()}
                        rows={getActivityRowsPerPage()}
                        totalRecords={activityTotalRecords}
                        onPageChange={onActivityPageChange}
                        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
                        pageLinkSize={5}
                      />
                    </div>
                  )}
              </>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default observer(BidAnalysisDashboard);
